import { faArrowDown, faArrowUp, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Alert, Form, Modal, Spinner, Table } from "react-bootstrap";
import {
    AttendanceAuditAction,
    IAttendanceAuditRecord,
} from "../../models/AttendanceConfiguration";
import { ServiceManager } from "../../services/ServiceManager";
import "../../assets/css/attendance-configuration.scss";
import AttendanceUkDateInput from "../establishments/config/attendance/attendance-uk-date-input";
import { toIsoDateFromUkDate } from "../establishments/config/attendance/attendance-utils";
import Layout from "../layout";

type AuditSortDirection = "ascending" | "descending";

const auditCategories = [
    { label: "Attendance Policy", available: true },
    { label: "MIS Client", available: false },
    { label: "Tasks", available: false },
    { label: "User management", available: false },
] as const;

function formatTimestamp(value: string): string {
    return format(new Date(value), "dd/MM/yyyy HH:mm");
}

function getAuditAction(record: IAttendanceAuditRecord): AttendanceAuditAction {
    return record.action ?? "Updated";
}

export default function Audit() {
    const [records, setRecords] = useState<IAttendanceAuditRecord[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [userFilter, setUserFilter] = useState("");
    const [actionFilter, setActionFilter] = useState<"all" | AttendanceAuditAction>("all");
    const [sectionFilter, setSectionFilter] = useState("all");
    const [sortDirection, setSortDirection] = useState<AuditSortDirection>("descending");
    const [expandedRecord, setExpandedRecord] = useState<IAttendanceAuditRecord | null>(null);

    useEffect(() => {
        let isCurrent = true;

        ServiceManager.AttendanceConfigurationService.GetAllAuditHistory()
            .then((loadedRecords) => {
                if (isCurrent) {
                    setRecords(loadedRecords);
                }
            })
            .catch((error: unknown) => {
                if (isCurrent) {
                    setLoadError(error instanceof Error ? error.message : "Unable to load audit history.");
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, []);

    const sections = Array.from(new Set(records.map((record) => record.changedSection))).sort();
    const dateFromIso = toIsoDateFromUkDate(dateFrom);
    const dateToIso = toIsoDateFromUkDate(dateTo);
    const filteredRecords = records
        .filter((record) => (
            (dateFromIso === null || record.changedAt.slice(0, 10) >= dateFromIso)
            && (dateToIso === null || record.changedAt.slice(0, 10) <= dateToIso)
            && (userFilter.length === 0 || record.actor.toLowerCase().includes(userFilter.toLowerCase()))
            && (actionFilter === "all" || getAuditAction(record) === actionFilter)
            && (sectionFilter === "all" || record.changedSection === sectionFilter)
        ))
        .sort((left, right) => (
            sortDirection === "descending"
                ? right.changedAt.localeCompare(left.changedAt)
                : left.changedAt.localeCompare(right.changedAt)
        ));
    const hasFilters = dateFrom.length > 0
        || dateTo.length > 0
        || userFilter.length > 0
        || actionFilter !== "all"
        || sectionFilter !== "all";

    const clearFilters = () => {
        setDateFrom("");
        setDateTo("");
        setUserFilter("");
        setActionFilter("all");
        setSectionFilter("all");
    };

    return (
        <Layout title="Audit">
            <div className="audit-workspace">
                <nav className="audit-category-tabs" aria-label="Audit categories">
                    {auditCategories.map((category) => (
                        <button
                            aria-current={category.available ? "page" : undefined}
                            className={category.available ? "is-active" : "is-coming-soon"}
                            disabled={!category.available}
                            key={category.label}
                            type="button"
                        >
                            <span>{category.label}</span>
                            {!category.available && <small>Coming soon</small>}
                        </button>
                    ))}
                </nav>
                <section className="audit-records" aria-labelledby="attendance-audit-heading">
                    <header className="audit-records-header">
                        <div>
                            <h2 id="attendance-audit-heading">Attendance Policy</h2>
                            <p>Policy changes made by MIS Central users.</p>
                        </div>
                        {hasFilters && (
                            <button className="audit-filter-reset audit-header-filter-reset" onClick={clearFilters} type="button">Clear filters</button>
                        )}
                    </header>
                    <div className="audit-filter-bar">
                        <div className="attendance-field audit-section-filter">
                            <Form.Select aria-label="Section" id="audit-section-filter" onChange={(event) => setSectionFilter(event.target.value)} value={sectionFilter}>
                                <option value="all">All sections</option>
                                {sections.map((section) => <option key={section} value={section}>{section}</option>)}
                            </Form.Select>
                        </div>
                        <div className="attendance-field audit-action-filter">
                            <Form.Select aria-label="Action" id="audit-action-filter" onChange={(event) => setActionFilter(event.target.value as "all" | AttendanceAuditAction)} value={actionFilter}>
                                <option value="all">All actions</option>
                                <option value="Created">Created</option>
                                <option value="Updated">Updated</option>
                                <option value="Deleted">Deleted</option>
                                <option value="Enabled">Enabled</option>
                                <option value="Disabled">Disabled</option>
                            </Form.Select>
                        </div>
                        <div className="attendance-field audit-date-filter">
                            <AttendanceUkDateInput
                                id="audit-date-from"
                                label="Start date"
                                onChange={setDateFrom}
                                placeholder="Start date"
                                value={dateFrom}
                            />
                        </div>
                        <div className="attendance-field audit-date-filter">
                            <AttendanceUkDateInput
                                id="audit-date-to"
                                label="End date"
                                onChange={setDateTo}
                                placeholder="End date"
                                value={dateTo}
                            />
                        </div>
                        <div className="attendance-field audit-user-filter">
                            <FontAwesomeIcon aria-hidden="true" className="audit-user-search-icon" icon={faSearch} />
                            <Form.Control aria-label="User" id="audit-user-filter" onChange={(event) => setUserFilter(event.target.value)} placeholder="Search email" value={userFilter} />
                        </div>
                    </div>
                    {isLoading && (
                        <div className="audit-loading"><Spinner animation="border" role="status" /></div>
                    )}
                    {!isLoading && loadError && <Alert className="mb-0" variant="danger">{loadError}</Alert>}
                    {!isLoading && !loadError && (
                        <Table responsive hover className="attendance-data-table audit-records-table mb-0">
                            <caption className="visually-hidden">Attendance Policy audit history.</caption>
                            <thead>
                                <tr>
                                    <th scope="col">
                                        <button
                                            aria-label={`Sort timestamp ${sortDirection === "descending" ? "oldest first" : "newest first"}`}
                                            className="audit-timestamp-sort"
                                            onClick={() => setSortDirection(sortDirection === "descending" ? "ascending" : "descending")}
                                            type="button"
                                        >
                                            Timestamp <FontAwesomeIcon aria-hidden="true" icon={sortDirection === "descending" ? faArrowDown : faArrowUp} />
                                        </button>
                                    </th>
                                    <th scope="col">User</th>
                                    <th scope="col">Action</th>
                                    <th scope="col">Section</th>
                                    <th scope="col">Before to After</th>
                                    <th scope="col">Establishment</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecords.map((record) => {
                                    const changeSummary = `${record.beforeValue} -> ${record.afterValue}`;
                                    const needsFullView = changeSummary.length > 92;

                                    return (
                                        <tr key={record.id}>
                                            <td>{formatTimestamp(record.changedAt)}</td>
                                            <td>{record.actor}</td>
                                            <td><span className={`audit-action-badge is-${getAuditAction(record).toLowerCase()}`}>{getAuditAction(record)}</span></td>
                                            <td>{record.changedSection}</td>
                                            <td>
                                                <span className="audit-change-summary">{changeSummary}</span>
                                                {needsFullView && (
                                                    <button className="audit-view-full" onClick={() => setExpandedRecord(record)} type="button">View full</button>
                                                )}
                                            </td>
                                            <td>{record.establishmentName ?? "Not supplied"}</td>
                                        </tr>
                                    );
                                })}
                                {filteredRecords.length === 0 && (
                                    <tr><td className="attendance-monitor-empty" colSpan={6}>No audit records match the current filters.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    )}
                </section>
            </div>
            <Modal centered onHide={() => setExpandedRecord(null)} show={Boolean(expandedRecord)}>
                <Modal.Header closeButton>
                    <Modal.Title>Audit change detail</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {expandedRecord && (
                        <div className="audit-change-detail">
                            <div><span>Timestamp</span><strong>{formatTimestamp(expandedRecord.changedAt)}</strong></div>
                            <div><span>User</span><strong>{expandedRecord.actor}</strong></div>
                            <div><span>Action</span><strong>{getAuditAction(expandedRecord)}</strong></div>
                            <div><span>Section</span><strong>{expandedRecord.changedSection}</strong></div>
                            <div><span>Before</span><strong>{expandedRecord.beforeValue}</strong></div>
                            <div><span>After</span><strong>{expandedRecord.afterValue}</strong></div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </Layout>
    );
}